/**
 * Shared availability search bar — check-in / check-out / guests inputs that
 * navigate to the properties list (or a supplied target) with the query params
 * `PropertiesList` reads (`check_in`, `check_out`, `guests`).
 *
 * Used by both the lodging `availability_search` homepage section
 * (`LodgingSections.tsx`) and the `search_forward` hero variant
 * (`HeroSection.tsx`) so the two stay in lock-step. Both render it through
 * `LodgingHero`, which floats this card over the hero's bottom edge.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import AvailabilityCalendar from './AvailabilityCalendar';

interface AvailabilitySearchBarProps {
  /** Where the Search button navigates; query params are appended. */
  targetHref?: string;
  /** Overrides the default "Search" CTA text. */
  ctaLabel?: string;
  showGuestCount?: boolean;
  className?: string;
  /** Property whose availability the calendar popover should load, so booked
   * dates render blocked (single-property hero). Without it the calendar
   * can't know what's booked — dates are validated downstream instead
   * (multi-property search filters the list server-side). */
  slug?: string;
  /** When provided, called with the selected dates/guests INSTEAD of
   * navigating to `targetHref`. The single-property hero uses this to seed the
   * on-page booking widget (navigating to /properties would just bounce back
   * to `/`, a dead end). */
  onSubmit?: (value: { checkIn: string; checkOut: string; guests: number }) => void;
}

export default function AvailabilitySearchBar({
  targetHref = '/properties',
  ctaLabel,
  showGuestCount = true,
  className,
  slug,
  onSubmit,
}: AvailabilitySearchBarProps) {
  const { t } = useTranslation('properties');
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(1);
  const [showCal, setShowCal] = useState(false);
  const calRef = useRef<HTMLDivElement>(null);

  // Click-outside to dismiss calendar popover
  useEffect(() => {
    if (!showCal) return;
    const handler = (e: MouseEvent) => {
      if (calRef.current && !calRef.current.contains(e.target as Node)) setShowCal(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showCal]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit({ checkIn, checkOut, guests: showGuestCount ? guests : 1 });
      return;
    }
    const qs = new URLSearchParams();
    if (checkIn) qs.set('check_in', checkIn);
    if (checkOut) qs.set('check_out', checkOut);
    if (showGuestCount && guests > 0) qs.set('guests', String(guests));
    navigate(`${targetHref}${qs.toString() ? `?${qs.toString()}` : ''}`);
  };

  // Inputs are intentionally borderless inside the card; keyboard focus is
  // indicated by the global *:focus-visible ring (index.css) — do NOT add
  // focus:outline-none / focus:ring-0 here, they suppress it.
  const inputClass = 'w-full rounded-lg border-0 bg-transparent px-3 py-2 text-sm';
  const labelClass = 'block px-3 pt-2 text-xs font-semibold uppercase tracking-wide text-ink-soft';

  return (
    <form
      onSubmit={submit}
      className={cn(
        'mx-auto grid max-w-3xl grid-cols-1 items-end gap-2 rounded-2xl border border-line bg-surface p-3 text-ink shadow-xl md:grid-cols-[1fr_auto_auto] md:p-2',
        className,
      )}
    >
      <div className="relative" ref={calRef}>
        <button
          type="button"
          onClick={() => setShowCal((v) => !v)}
          className={cn('w-full text-left rounded-lg border-0 bg-transparent px-3 py-2 text-sm', checkIn && checkOut ? 'text-ink' : 'text-ink-muted')}
        >
          <span className={labelClass}>{t('list.form.dates')}</span>
          <span className="block mt-0.5">
            {checkIn && checkOut
              ? `${checkIn} → ${checkOut}`
              : t('list.form.selectDates')}
          </span>
        </button>
        {showCal && (
          <div className="absolute top-full left-0 mt-2 z-20 bg-surface border border-line rounded-2xl shadow-xl p-4">
            <AvailabilityCalendar
              slug={slug}
              checkIn={checkIn || null}
              checkOut={checkOut || null}
              onSelect={({ checkIn: ci, checkOut: co }) => {
                setCheckIn(ci || '');
                setCheckOut(co || '');
                if (ci && co) setShowCal(false);
              }}
              monthsToShow={1}
            />
          </div>
        )}
      </div>
      {showGuestCount && (
        <label className="text-left md:w-28">
          <span className={labelClass}>{t('list.form.guests')}</span>
          <input
            type="number"
            min={1}
            value={guests}
            onChange={(e) => setGuests(Math.max(1, Number(e.target.value) || 1))}
            className={inputClass}
          />
        </label>
      )}
      <button
        type="submit"
        className="m-1 inline-flex items-center justify-center gap-2 rounded-lg bg-cta px-5 py-3 font-medium text-cta-fg transition-colors hover:bg-cta-deep"
      >
        <Search className="h-4 w-4" aria-hidden="true" />
        {ctaLabel ?? t('list.form.search')}
      </button>
    </form>
  );
}
